import React, { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { ADDED_ASSET_RULES, type AddedAssetTheme } from 'fym-shared/src/music24/addedAssets';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../components/primitives/SMLButton';
import { addedAssetsMessages as messages } from './addedAssetsMessages';
import styles from './UploadDropZone.module.scss';

interface UploadDropZoneProps {
  themeId: AddedAssetTheme;
  onFile: (file: File) => void;
}

/** The `accept` of the file input: the types of the rule, and the extensions for a system that has no type for a MOV. */
function acceptOf(themeId: AddedAssetTheme): string {
  const types = ADDED_ASSET_RULES[themeId].contentTypes;
  const extensions = types.includes('video/quicktime') ? ['.mp4', '.mov'] : ['.mp4'];
  return [...types, ...extensions].join(',');
}

/** The pick of a file (design a4): a drop zone and "Choose a file". */
const UploadDropZoneComponent: React.FC<UploadDropZoneProps> = ({ themeId, onFile }) => {
  const intl = useIntl();
  const input = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // The same file can be picked again after a failure.
      // eslint-disable-next-line no-param-reassign
      event.target.value = '';
      if (file !== undefined) {
        onFile(file);
      }
    },
    [onFile]
  );
  const onDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsOver(false);
      const file = event.dataTransfer.files[0];
      if (file !== undefined) {
        onFile(file);
      }
    },
    [onFile]
  );

  return (
    <div
      className={clsx(styles.drop, isOver && styles.dropOver)}
      onDragOver={(event) => {
        event.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={onDrop}
      data-testid="added-drop-zone"
    >
      <b>{intl.formatMessage(messages.dropTitle)}</b>
      <SMLButton onClick={() => input.current?.click()}>{intl.formatMessage(messages.chooseFile)}</SMLButton>
      <input
        ref={input}
        type="file"
        accept={acceptOf(themeId)}
        hidden
        aria-label={intl.formatMessage(messages.chooseFile)}
        onChange={onChange}
        data-testid="added-file-input"
      />
    </div>
  );
};

export const UploadDropZone = TypedMemo(UploadDropZoneComponent);
